import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Spinner from '../Spinner';

const CustomVideoPlayer = ({
  id,
  credentials,
  height,
  style,
  setStatus,
  setMessage,
}) => {
  const { t, i18n } = useTranslation();
  const videoRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let hls;
    let refreshTimer;
    setError('');
    setIsLoading(true);

    const loadHLS = credentials => {
      if (Hls.isSupported() && videoRef.current) {
        hls = new Hls({
          debug: process.env.NODE_ENV === 'development',
          xhrSetup: xhr => {
            if (!credentials.username || !credentials.password) return;

            const cred = btoa(
              `${credentials.username}:${credentials.password}`,
            );
            xhr.setRequestHeader('Authorization', `Basic ${cred}`);
          },
          lowLatencyMode: true,
          liveSyncDurationCount: 1,
          liveMaxLatencyDurationCount: 3,
          maxLiveSyncPlaybackRate: 1.5,
        });

        hls.loadSource(credentials.url);
        hls.attachMedia(videoRef.current);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          videoRef.current.muted = true;
          videoRef.current.play();
          setIsLoading(false);
          setStatus?.('Connected');
          setMessage?.('');
          setError('');
        });
        hls.on(Hls.Events.ERROR, (err, data) => {
          console.error(err);
          if (data.fatal) {
            setIsLoading(false);
            setStatus?.('Error');
            setMessage?.(data.error.message);
            setError(data.error.message);
          }
        });
      }
    };

    const fetchURL = async () => {
      const headers = new Headers();

      if (credentials.username && credentials.password) {
        const cred = btoa(`${credentials.username}:${credentials.password}`);
        headers.set('Authorization', `Basic ${cred}`);
      }

      try {
        const response = await fetch(credentials.url, { headers });
        if (response.ok) {
          if (response.url.includes('.m3u8')) {
            if (hls) {
              hls.destroy();
              hls = null;
            }
            loadHLS({ url: response.url });
          } else {
            const video = videoRef.current;
            video.src = response.url;
            video.addEventListener('loadedmetadata', () => {
              video.play();
              setIsLoading(false);
              setStatus?.('Connected');
              setMessage?.('');
              setError('');
            });
          }
        } else {
          const payload = await response.json();
          setIsLoading(false);
          setStatus?.('Error');
          const valMsg = `validation.${payload?.message}`;
          const err =
            (i18n.exists(valMsg) ? t(valMsg) : null) ??
            response.statusText ??
            response.status;
          setMessage?.(err);
          setError(err);
          console.error(`Error fetching video URL: ${err}`);
        }
      } catch (err) {
        setIsLoading(false);
        setStatus?.('Error');
        const errMsg = err.message;
        setMessage?.(errMsg);
        setError(errMsg);
        console.error(`Error fetching video URL: ${errMsg}`);
      }
    };

    const setup = async () => {
      const isHLS = credentials.url?.includes('hls');

      if (isHLS) {
        loadHLS(credentials);
      } else if (
        videoRef.current.canPlayType('application/vnd.apple.mpegurl') ||
        videoRef.current.canPlayType('video/mp4')
      ) {
        await fetchURL();
        // refresh every 4½ minutes (270 s) to stay ahead of 300 s expiry
        if (hls) refreshTimer = setInterval(fetchURL, 270 * 1000);
      }
    };
    setup();

    return () => {
      clearInterval(refreshTimer);
      if (hls) hls.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        height: height ?? '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {(isLoading || error) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: height ?? '100%',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {isLoading && <Spinner />}
          {error && (
            <Typography
              color="error"
              textAlign="center"
              fontWeight="bold"
              width="100%"
            >
              {error}
            </Typography>
          )}
        </Box>
      )}
      <video
        id={id}
        ref={videoRef}
        controls
        autoPlay
        loop
        style={{
          width: '100%',
          borderRadius: '10px',
          height: height ?? '100%',
          ...style,
        }}
      />
    </Box>
  );
};

CustomVideoPlayer.defaultProps = {
  height: null,
  style: {},
};

CustomVideoPlayer.propTypes = {
  id: PropTypes.string.isRequired,
  credentials: PropTypes.shape({
    url: PropTypes.string,
    username: PropTypes.string,
    password: PropTypes.string,
  }).isRequired,
  height: PropTypes.string,
  style: PropTypes.object,
  setStatus: PropTypes.func,
  setMessage: PropTypes.func,
};

export default CustomVideoPlayer;
